import React, { useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useOnClickOutside } from '../../../../../helpers/hooks/useOnClickOutside';
import { colourNeutral02, colourNeutral09, colourNeutral10 } from '../../../../../styling/colours';
import { fontFamily, fontSize02 } from '../../../../../styling/fonts';
import { shadow2 } from '../../../../../styling/shadows';
import { spacing03, spacing04 } from '../../../../../styling/spacing';

type Props = {
  fillDownColumn: () => void;
  children: React.ReactNode;
};

export const RoomsHeatLossTableColumnHeaderContextMenu = ({ fillDownColumn, children }: Props) => {
  const [{ isOpen, menuBottomPixels, menuLeftPixels }, setMenuState] = useState<{
    isOpen: boolean;
    menuBottomPixels: number;
    menuLeftPixels: number;
  }>({
    isOpen: false,
    menuBottomPixels: 0,
    menuLeftPixels: 0,
  });
  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setMenuState(prevState => ({ ...prevState, isOpen: false })));

  return (
    <Container
      onClick={event => {
        const boundingClientRect = (event.target as HTMLElement).getBoundingClientRect();

        setMenuState({
          isOpen: true,
          menuBottomPixels: boundingClientRect.bottom - event.clientY,
          menuLeftPixels: event.clientX - boundingClientRect.left,
        });
      }}
    >
      {isOpen && (
        <Menu ref={menuRef} $menuBottomPixels={menuBottomPixels} $menuLeftPixels={menuLeftPixels}>
          <MenuButtonItem
            type="button"
            onClick={event => {
              fillDownColumn();

              // We stop propagation to prevent the click event from bubbling up and triggering onClick on the Container
              event.stopPropagation();

              setMenuState(prevState => ({ ...prevState, isOpen: false }));
            }}
          >
            Fill Down
          </MenuButtonItem>
        </Menu>
      )}
      <ChildrenContainer>{children}</ChildrenContainer>
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  align-items: center;
  position: relative;
  height: 100%;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${colourNeutral09};
  }
`;

const Menu = styled.div<{ $menuBottomPixels: number; $menuLeftPixels: number }>`
  box-shadow: ${shadow2};
  position: absolute;
  background-color: white;
  width: 250px;
  left: ${props => props.$menuLeftPixels}px;
  bottom: ${props => props.$menuBottomPixels}px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  z-index: 10;
`;

export const MenuButtonItem = styled.button`
  font-family: ${fontFamily};
  font-size: ${fontSize02};
  outline: none;
  border: none;
  background-color: transparent;
  text-align: left;
  color: ${colourNeutral02};
  padding: ${spacing03} ${spacing04};
  transition: all 0.25s ease;
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  &:hover,
  &:focus {
    background-color: ${colourNeutral10};
  }
`;

const ChildrenContainer = styled.div``;
